
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as entity from '../entity'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {ClientReportType} from '../core/ClientReportType';
import {FieldModelClientReportType} from '../core/ClientReportType';
import {ClientReportStatus} from './ClientReportStatus';
import {FieldModelClientReportStatus} from './ClientReportStatus';

/**
 * ClientReport struct
 */
class ClientReport {
  /**
   * Initialize struct
   * @param {!number=} recordId
   * @param {!number=} accountLogin
   * @param {!ClientReportType=} type
   * @param {!ClientReportStatus=} status
   * @constructor
   */
  constructor (recordId = new UInt64(0, 0), accountLogin = new UInt64(0, 0), type = new ClientReportType(), status = new ClientReportStatus()) {
    this.RecordId = recordId
    this.AccountLogin = accountLogin
    this.Type = type
    this.Status = status
  }

  /**
   * Copy struct (shallow copy)
   * @this {!ClientReport}
   * @param {!ClientReport} other Other struct
   * @returns {!ClientReport} This struct
   */
  copy (other) {
    if (other.RecordId != null) {
      this.RecordId = UInt64.fromNumber(other.RecordId)
    } else {
      this.RecordId = null
    }
    if (other.AccountLogin != null) {
      this.AccountLogin = UInt64.fromNumber(other.AccountLogin)
    } else {
      this.AccountLogin = null
    }
    if (other.Type != null) {
      this.Type = ClientReportType.fromObject(other.Type)
    } else {
      this.Type = null
    }
    if (other.Status != null) {
      this.Status = ClientReportStatus.fromObject(other.Status)
    } else {
      this.Status = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!ClientReport}
   * @returns {!ClientReport} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new ClientReportModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new ClientReportModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!ClientReport}
   * @param {!ClientReport} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof ClientReport)) {
      throw new TypeError('Instance of ClientReport is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!ClientReport}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      RecordId: ((this.RecordId != null) ? this.RecordId.toNumber() : null),
      AccountLogin: ((this.AccountLogin != null) ? this.AccountLogin.toNumber() : null),
      Type: ((this.Type != null) ? this.Type : null),
      Status: ((this.Status != null) ? this.Status : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return ClientReport.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!ClientReport} other Object value
   * @returns {!ClientReport} Created struct
   */
  static fromObject (other) {
    return new ClientReport().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!ClientReport}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return ClientReport.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!ClientReport}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 3027
  }
}

export { ClientReport };

/**
 * Fast Binary Encoding ClientReport field model
 */
class FieldModelClientReport extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.RecordId = new fbe.FieldModelUInt64(buffer, 4 + 4)
    this.AccountLogin = new fbe.FieldModelUInt64(buffer, this.RecordId.fbeOffset + this.RecordId.fbeSize)
    this.Type = new FieldModelClientReportType(buffer, this.AccountLogin.fbeOffset + this.AccountLogin.fbeSize)
    this.Status = new FieldModelClientReportStatus(buffer, this.Type.fbeOffset + this.Type.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelClientReport}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelClientReport}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.RecordId.fbeSize + this.AccountLogin.fbeSize + this.Type.fbeSize + this.Status.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelClientReport}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.RecordId.fbeExtra + this.AccountLogin.fbeExtra + this.Type.fbeExtra + this.Status.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelClientReport}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelClientReport.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelClientReport}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 3027
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelClientReport}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelClientReport}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.RecordId.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.RecordId.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.RecordId.fbeSize

    if ((fbeCurrentSize + this.AccountLogin.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.AccountLogin.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.AccountLogin.fbeSize

    if ((fbeCurrentSize + this.Type.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Type.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Type.fbeSize

    if ((fbeCurrentSize + this.Status.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Status.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Status.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelClientReport}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelClientReport}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelClientReport}
   * @param {!ClientReport} fbeValue Default value, defaults is new ClientReport()
   * @returns {!ClientReport} ClientReport value
   */
  get (fbeValue = new ClientReport()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelClientReport}
   * @param {!ClientReport} fbeValue ClientReport value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.RecordId.fbeSize) <= fbeStructSize) {
      fbeValue.RecordId = this.RecordId.get()
    } else {
      fbeValue.RecordId = new UInt64(0, 0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.RecordId.fbeSize

    if ((fbeCurrentSize + this.AccountLogin.fbeSize) <= fbeStructSize) {
      fbeValue.AccountLogin = this.AccountLogin.get()
    } else {
      fbeValue.AccountLogin = new UInt64(0, 0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.AccountLogin.fbeSize

    if ((fbeCurrentSize + this.Type.fbeSize) <= fbeStructSize) {
      fbeValue.Type = this.Type.get()
    } else {
      fbeValue.Type = new ClientReportType()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Type.fbeSize

    if ((fbeCurrentSize + this.Status.fbeSize) <= fbeStructSize) {
      fbeValue.Status = this.Status.get()
    } else {
      fbeValue.Status = new ClientReportStatus()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Status.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelClientReport}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelClientReport}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelClientReport}
   * @param {!ClientReport} fbeValue ClientReport value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelClientReport}
   * @param {ClientReport} fbeValue ClientReport value
   */
  setFields (fbeValue) {
    this.RecordId.set(fbeValue.RecordId)
    this.AccountLogin.set(fbeValue.AccountLogin)
    this.Type.set(fbeValue.Type)
    this.Status.set(fbeValue.Status)
  }
}

export { FieldModelClientReport };

/**
 * Fast Binary Encoding ClientReport model
 */
class ClientReportModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelClientReport(this.buffer, 4)
  }

  /**
   * Get the ClientReport model
   * @this {!ClientReportModel}
   * @returns {!FieldModelClientReport} model ClientReport model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!ClientReportModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!ClientReportModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return ClientReportModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!ClientReportModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelClientReport.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!ClientReportModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!ClientReportModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!ClientReportModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!ClientReportModel}
   * @param {!ClientReport} value ClientReport value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!ClientReportModel}
   * @param {!ClientReport} value ClientReport value, defaults is new ClientReport()
   * @return {!object} Deserialized ClientReport value and its size
   */
  deserialize (value = new ClientReport()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new ClientReport(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new ClientReport(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!ClientReportModel}
   * @param {!number} prev Previous ClientReport model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { ClientReportModel };
